import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiV1, setAccessToken } from '@/lib/api';

const AuthCtx = createContext({
  user: null,
  loading: true,
  isAuthenticated: false,
  hasRole: () => false,
  hasPermission: () => false,
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await apiV1.post('/auth/refresh');
      setAccessToken(r.data.access_token);
      setUser(r.data.user);
      return r.data.user;
    } catch {
      setAccessToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try { await apiV1.post('/auth/logout'); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    window.location.assign('/login');
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      hasRole: (...roles) => !!user && roles.includes(user.role),
      hasPermission: (perm) =>
        !!user && (user.permissions?.includes('*') || user.permissions?.includes(perm)),
      refresh,
      logout,
      setUser,
    }),
    [user, loading, refresh, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
